/**
 * Run history — `bimgo-ai-web`'s `SkillExecutionHistoryPanel.tsx`: a list of
 * runs (status pill, version, when, how long) that opens into one run's detail
 * (the four facts, the error, the parameters, the display view, and the raw
 * result behind a dialog). Served from the agent's local DB.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { ExecutionDoc, ExecutionSummary, SkillsClient } from "../../lib/skills";
import { Modal } from "../settings/Modal";
import { ExecutionDisplayView, isDisplayPayload } from "./ExecutionDisplayView";
import { JsonView } from "./JsonView";
import { CheckIcon, ChevronLeftIcon, ClockIcon, LoaderIcon, XIcon } from "./icons";
import { formatDate } from "./SkillVersionsPanel";

type ListState = { kind: "loading" } | { kind: "error" } | { kind: "loaded"; items: ExecutionSummary[] };
type DetailState = { kind: "loading" } | { kind: "error" } | { kind: "loaded"; doc: ExecutionDoc };

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`;
}

export function StatusPill({ success, timedOut }: { success: boolean; timedOut: boolean }) {
  const { t } = useLocale();
  return (
    <span className={`exec-status${success ? " is-ok" : " is-bad"}`}>
      {success ? <CheckIcon size={12} /> : <XIcon size={12} />}
      {success ? t("skillHistory.success") : timedOut ? t("skillHistory.timedOut") : t("skillHistory.failed")}
    </span>
  );
}

export function SkillHistoryPanel({ client, skillId, refreshKey }: { client: SkillsClient; skillId: string; refreshKey: number }) {
  const { t, locale } = useLocale();
  const [list, setList] = useState<ListState>({ kind: "loading" });
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ kind: "loading" });
  const [rawOpen, setRawOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setList({ kind: "loading" });
    client
      .executions(skillId, 20)
      .then((items) => {
        if (!cancelled) setList({ kind: "loaded", items });
      })
      .catch(() => {
        if (!cancelled) setList({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [client, skillId, refreshKey]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setDetail({ kind: "loading" });
    client
      .execution(skillId, selected)
      .then((doc) => {
        if (!cancelled) setDetail({ kind: "loaded", doc });
      })
      .catch(() => {
        if (!cancelled) setDetail({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [client, skillId, selected]);

  if (selected) {
    return (
      <div className="exec-detail">
        <div className="exec-detail-head">
          <button type="button" className="icon-btn" onClick={() => setSelected(null)} aria-label={t("common.back")}>
            <ChevronLeftIcon />
          </button>
          {detail.kind === "loaded" ? <StatusPill success={detail.doc.success} timedOut={detail.doc.timedOut} /> : null}
        </div>
        {detail.kind === "loading" ? (
          <div className="skills-center muted"><LoaderIcon size={14} /> {t("common.loading")}</div>
        ) : detail.kind === "error" ? (
          <div className="skills-center err">{t("skillHistory.detailLoadError")}</div>
        ) : (
          <DetailBody doc={detail.doc} locale={locale} onViewRaw={() => setRawOpen(true)} />
        )}
        {detail.kind === "loaded" ? (
          <Modal open={rawOpen} onClose={() => setRawOpen(false)} title={t("skillHistory.resultTitle")} size="4xl">
            <JsonView text={JSON.stringify(detail.doc.result ?? null)} />
          </Modal>
        ) : null}
      </div>
    );
  }

  if (list.kind === "loading") return <div className="skills-center muted"><LoaderIcon size={14} /> {t("common.loading")}</div>;
  if (list.kind === "error") return <div className="skills-center err">{t("skillHistory.loadError")}</div>;
  if (list.items.length === 0) {
    return (
      <div className="skills-empty">
        <span className="skills-empty-glyph"><ClockIcon size={22} /></span>
        <p className="skills-empty-title">{t("skillHistory.empty")}</p>
        <p className="skills-empty-desc">{t("skillHistory.emptyDesc")}</p>
      </div>
    );
  }
  return (
    <ul className="exec-rows">
      {list.items.map((item) => (
        <li key={item.executionId}>
          <button type="button" className="exec-row" onClick={() => setSelected(item.executionId)}>
            <span className="exec-row-main">
              <StatusPill success={item.success} timedOut={item.timedOut} />
              {item.skillVersion != null ? <span className="exec-row-version">v{item.skillVersion}</span> : null}
              {item.revitVersion ? <span className="exec-row-version">Revit {item.revitVersion}</span> : null}
            </span>
            <span className="exec-row-meta">
              <span>{formatDate(item.executedAt, locale)}</span>
              <span>{formatDuration(item.durationMs)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function DetailBody({ doc, locale, onViewRaw }: { doc: ExecutionDoc; locale: string; onViewRaw: () => void }) {
  const { t } = useLocale();
  const fields: Array<[string, string]> = [
    [t("skillHistory.executedAt"), formatDate(doc.executedAt, locale)],
    [t("skillHistory.duration"), formatDuration(doc.durationMs)],
    [t("skillHistory.revitVersion"), doc.revitVersion ?? "—"],
    [t("skillHistory.skillVersion"), doc.skillVersion != null ? `v${doc.skillVersion}` : "—"],
  ];
  const parameters = doc.parametersByName ?? doc.parameters;
  const hasParameters = Array.isArray(doc.parameters) ? doc.parameters.length > 0 : !!parameters;
  return (
    <>
      {!doc.success && doc.errorMessage ? <div className="exec-error">{doc.errorMessage}</div> : null}
      <dl className="exec-facts">
        {fields.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd title={value}>{value}</dd>
          </div>
        ))}
      </dl>
      {hasParameters ? (
        <div>
          <p className="exec-label">{t("skillHistory.parameters")}</p>
          <pre className="exec-params">{JSON.stringify(parameters, null, 2)}</pre>
        </div>
      ) : null}
      {isDisplayPayload(doc.display) ? (
        <ExecutionDisplayView display={doc.display} onViewRawResult={onViewRaw} />
      ) : (
        <button type="button" className="settings-btn secondary is-small" onClick={onViewRaw}>
          <FileJsonIcon />
          {t("skillHistory.viewResult")}
        </button>
      )}
    </>
  );
}

/** lucide `FileJson` — the old "View result" button's glyph. */
function FileJsonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 12a1 1 0 0 0-1 1v1a1 1 0 0 1-1 1 1 1 0 0 1 1 1v1a1 1 0 0 0 1 1" />
      <path d="M14 18a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1 1 1 0 0 1-1-1v-1a1 1 0 0 0-1-1" />
    </svg>
  );
}
